import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { protectedResources } from '../auth/auth.config';
import { SKIP_GLOBAL_LOADING } from '../interceptor/http-context.tokens';
import { ObservableTtlCache } from '../utils/observable-ttl-cache';
import {
  AutoAssignDeviceTypeListDto,
  LicenseBatchDto,
  LicenseBatchListDto,
  LicenseBatchRequestDto,
  LicenseProjection,
  ReleaseBundleDto,
  ReleaseBundlesResponseDto,
  ReleaseDto,
  ReleaseListDto,
  ScopeListDto,
} from '../models/license.model';
import { ArtifactResponse, ArtifactVM } from '../models/device.model';

export interface SaveLicenseBatchOptions {
  dryRun?: boolean;
  projection?: LicenseProjection;
}

@Injectable({
  providedIn: 'root',
})
export class LicenseBatchService {
  private apiEndpoint: string = protectedResources.apiTodoList.endpoint;
  private readonly productArtifactsCache = new ObservableTtlCache<ArtifactVM[]>(
    5 * 60_000,
  );

  constructor(private http: HttpClient) {}

  getAll(
    projection: LicenseProjection = 'BRIEF',
  ): Observable<LicenseBatchListDto> {
    const params = new HttpParams().set('projection', projection);
    return this.http.get<LicenseBatchListDto>(
      `${this.apiEndpoint}/licenses/batches/all`,
      {
        params,
      },
    );
  }

  getAllByCurrentCompanyId(
    projection: LicenseProjection = 'BRIEF',
  ): Observable<LicenseBatchListDto> {
    const params = new HttpParams().set('projection', projection);
    return this.http.get<LicenseBatchListDto>(
      `${this.apiEndpoint}/licenses/batches`,
      {
        params,
      },
    );
  }

  /**
   * The batches a licence could be taken from for this device: those of the company owning it.
   * The backend answers from the device itself, so this works for every role allowed to reach
   * the device - a masteradmin and a production engineer alike.
   */
  getAllForDevice(
    deviceId: string,
    projection: LicenseProjection = 'BRIEF',
  ): Observable<LicenseBatchListDto> {
    const normalizedDeviceId = deviceId?.trim();
    if (!normalizedDeviceId) {
      return of({ list: [] });
    }

    const params = new HttpParams()
      .set('forDeviceId', normalizedDeviceId)
      .set('projection', projection);
    return this.http.get<LicenseBatchListDto>(
      `${this.apiEndpoint}/licenses/batches/all`,
      {
        params,
      },
    );
  }

  getAllByCompanyId(
    companyId: string,
    projection: LicenseProjection = 'BRIEF',
  ): Observable<LicenseBatchListDto> {
    const normalizedCompanyId = companyId?.trim().toLowerCase();
    if (!normalizedCompanyId) {
      return of({ list: [] });
    }

    const params = new HttpParams()
      .set('companyId', normalizedCompanyId)
      .set('projection', projection);
    return this.http.get<LicenseBatchListDto>(
      `${this.apiEndpoint}/licenses/batches/all`,
      {
        params,
      },
    );
  }

  create(
    payload: LicenseBatchRequestDto,
    options: SaveLicenseBatchOptions = {},
  ): Observable<LicenseBatchDto> {
    return this.http.post<LicenseBatchDto>(
      `${this.apiEndpoint}/licenses/batches`,
      payload,
      this.toSaveRequestOptions(options),
    );
  }

  getById(
    batchId: string,
    projection: LicenseProjection = 'BRIEF',
  ): Observable<LicenseBatchDto> {
    const encodedBatchId = encodeURIComponent(batchId);
    const params = new HttpParams().set('projection', projection);
    return this.http.get<LicenseBatchDto>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}`,
      {
        params,
      },
    );
  }

  update(
    batchId: string,
    payload: LicenseBatchRequestDto,
    options: SaveLicenseBatchOptions = {},
  ): Observable<LicenseBatchDto> {
    const encodedBatchId = encodeURIComponent(batchId);
    return this.http.put<LicenseBatchDto>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}`,
      payload,
      this.toSaveRequestOptions(options),
    );
  }

  /**
   * A dry run stores nothing and answers with the batch as it would be saved. It also runs behind
   * the user's editing, so it must not raise the blocking fullscreen loader. Shared by create and
   * update so the two cannot drift apart.
   */
  private toSaveRequestOptions(options: SaveLicenseBatchOptions): {
    params: HttpParams;
    context: HttpContext;
  } {
    const { dryRun = false, projection } = options;
    let params = new HttpParams();
    if (dryRun) {
      params = params.set('dryRun', true);
    }
    if (projection) {
      params = params.set('projection', projection);
    }
    return {
      params,
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, dryRun),
    };
  }

  delete(batchId: string): Observable<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    return this.http.delete<void>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}`,
    );
  }

  assignDeviceToBatch(batchId: string, deviceId: string): Observable<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http.post<void>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}/device/${encodedDeviceId}`,
      null,
    );
  }

  removeDeviceFromBatch(batchId: string, deviceId: string): Observable<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const encodedDeviceId = encodeURIComponent(deviceId);
    return this.http.delete<void>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}/device/${encodedDeviceId}`,
    );
  }

  assignUserToBatch(batchId: string, userId: string): Observable<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const encodedUserId = encodeURIComponent(userId);
    return this.http.post<void>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}/users/${encodedUserId}`,
      null,
    );
  }

  removeUserFromBatch(batchId: string, userId: string): Observable<void> {
    const encodedBatchId = encodeURIComponent(batchId);
    const encodedUserId = encodeURIComponent(userId);
    return this.http.delete<void>(
      `${this.apiEndpoint}/licenses/batches/${encodedBatchId}/users/${encodedUserId}`,
    );
  }

  getScopes(): Observable<ScopeListDto> {
    return this.http.get<ScopeListDto>(
      `${this.apiEndpoint}/licenses/batches/scopes`,
    );
  }

  /** The device types a batch can be set to license automatically. */
  getAutoAssignDeviceTypes(): Observable<AutoAssignDeviceTypeListDto> {
    return this.http.get<AutoAssignDeviceTypeListDto>(
      `${this.apiEndpoint}/licenses/batches/device-types`,
    );
  }

  getProductArtifacts(projectId: string): Observable<ArtifactVM[]> {
    const normalizedProjectId = projectId.trim();
    return this.productArtifactsCache.get(normalizedProjectId, () =>
      this.http
        .get<
          ArtifactResponse[] | null
        >(`${this.apiEndpoint}/spadok/projects/${encodeURIComponent(normalizedProjectId)}/artifacts`)
        .pipe(
          map((response): ArtifactVM[] =>
            (response ?? [])
              .filter((artifact) => artifact.uploadStatus === 'UPLOADED')
              .map((artifact): ArtifactVM => {
                const scopes = artifact.scopes ?? [];
                return {
                  ...artifact,
                  scopes,
                  versionAndScopes: scopes.length
                    ? `${artifact.version} (${scopes.join(', ')})`
                    : artifact.version,
                };
              }),
          ),
        ),
    );
  }

  getReleases(): Observable<ReleaseDto[]> {
    return this.http
      .get<ReleaseListDto>(`${this.apiEndpoint}/spadok/releases`)
      .pipe(map((response) => response.releases ?? []));
  }

  getBundles(releaseId: string): Observable<ReleaseBundleDto[]> {
    return this.http
      .get<ReleaseBundlesResponseDto>(
        `${this.apiEndpoint}/spadok/releases/${encodeURIComponent(releaseId)}/bundles?projection=BASIC_WITH_ARTIFACTS`,
        {
          context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
        },
      )
      .pipe(map((response) => response.bundles ?? []));
  }
}
